import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, type Locale } from '../../hooks/useT';

function CGUBodyFr() {
  return (
    <>
      <h2>1. Objet</h2>
      <p>
        Les présentes CGU régissent l&apos;accès et l&apos;utilisation du jeu en ligne{' '}
        <strong>Dezoom</strong>, édité par PrograGames (Florian Augeard). En accédant au jeu,
        tu acceptes ces conditions.
      </p>
      <h2>2. Éditeur</h2>
      <p>
        PrograGames — Florian Augeard, 49000 Angers, France. Contact :{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
        .
      </p>
      <h2>3. Accès au jeu</h2>
      <p>
        Dezoom est un jeu de devinettes photo dans le navigateur. Le défi du jour, les archives
        et les classements sont gratuits et financés par la publicité (Google AdSense).
        Un abonnement premium optionnel supprime les publicités et soutient le développement
        (voir <Link to="/cgv">CGV</Link>). Une connexion Internet est requise.
      </p>
      <h2>4. Âge et capacité</h2>
      <p>
        Le jeu s&apos;adresse aux personnes de <strong>16 ans et plus</strong>. Si tu es mineur,
        l&apos;utilisation doit se faire avec l&apos;autorisation d&apos;un parent ou titulaire de l&apos;autorité parentale.
      </p>
      <h2>5. Compte joueur</h2>
      <ul>
        <li>Tu peux jouer en <strong>invité</strong> ou créer un <strong>compte</strong> (email, pseudo).</li>
        <li>Tu es responsable de la confidentialité de ton accès (lien magique, session).</li>
        <li>Le pseudo doit être unique et ne pas porter atteinte aux droits de tiers.</li>
        <li>La progression (série, scores, classements) est liée au compte connecté.</li>
      </ul>
      <h2>6. Fonctionnalités</h2>
      <ul>
        <li><strong>Défi du jour</strong> : une énigme officielle par jour, classement quotidien.</li>
        <li><strong>Archives</strong> : rejouer les énigmes passées ; la première partie compte pour le classement historique de l&apos;énigme, sans impact sur la série de victoires.</li>
        <li><strong>Classements</strong> : du jour, de la semaine, du mois et tous les temps.</li>
        <li><strong>Top dezoom</strong> : énigmes quotidiennes les plus aimées par la communauté.</li>
        <li><strong>Premium</strong> : expérience sans publicité et soutien au développement.</li>
      </ul>
      <h2>7. Règles de conduite</h2>
      <p>Il est interdit notamment de :</p>
      <ul>
        <li>Utiliser des bots, scripts ou outils automatisés ;</li>
        <li>Exploiter des bugs pour obtenir un avantage ;</li>
        <li>Harceler d&apos;autres joueurs ;</li>
        <li>Contourner les systèmes de score ou de classement ;</li>
        <li>Usurper l&apos;identité d&apos;autrui ou de l&apos;éditeur.</li>
      </ul>
      <h2>8. Abonnement premium</h2>
      <p>
        L&apos;abonnement premium est un service numérique lié à ton compte,
        non transférable. Il supprime l&apos;affichage publicitaire sur le site.
        Conditions de résiliation et remboursement : voir <Link to="/cgv">CGV</Link>.
      </p>
      <h2>9. Propriété intellectuelle</h2>
      <p>
        Le jeu, ses énigmes, marques et contenus restent la propriété de PrograGames ou de ses concédants
        (photographes Pexels/Unsplash — voir <Link to="/credits">crédits photos</Link>).
      </p>
      <h2>10. Données personnelles</h2>
      <p>
        Le traitement des données est décrit dans la{' '}
        <Link to="/confidentialite">politique de confidentialité</Link>.
      </p>
      <h2>11. Responsabilité</h2>
      <p>
        Le jeu est fourni « en l&apos;état ». PrograGames ne garantit pas un fonctionnement ininterrompu.
      </p>
      <h2>12. Droit applicable</h2>
      <p>Les présentes CGU sont soumises au <strong>droit français</strong>.</p>
    </>
  );
}

function CGUBodyEn() {
  return (
    <>
      <h2>1. Purpose</h2>
      <p>
        These Terms of Use govern access to and use of the online game{' '}
        <strong>Dezoom</strong>, published by PrograGames (Florian Augeard). By accessing the game,
        you accept these terms.
      </p>
      <h2>2. Publisher</h2>
      <p>
        PrograGames — Florian Augeard, 49000 Angers, France. Contact:{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
        .
      </p>
      <h2>3. Access to the game</h2>
      <p>
        Dezoom is a photo guessing game in the browser. The daily challenge, archives
        and leaderboards are free and funded by advertising (Google AdSense).
        An optional premium subscription removes ads and supports development
        (see <Link to="/cgv">Terms of sale</Link>). An Internet connection is required.
      </p>
      <h2>4. Age and capacity</h2>
      <p>
        The game is intended for users aged <strong>16 and over</strong>. Minors must use the service
        with parental or guardian consent.
      </p>
      <h2>5. Player account</h2>
      <ul>
        <li>You may play as a <strong>guest</strong> or create an <strong>account</strong> (email, display name).</li>
        <li>You are responsible for keeping your access credentials secure.</li>
        <li>Display names must be unique and must not infringe third-party rights.</li>
        <li>Progress (streak, scores, leaderboards) is tied to your logged-in account.</li>
      </ul>
      <h2>6. Features</h2>
      <ul>
        <li><strong>Daily challenge</strong>: one official puzzle per day with a daily leaderboard.</li>
        <li><strong>Archives</strong>: replay past daily puzzles; your first attempt counts toward that puzzle&apos;s historical leaderboard, without affecting your win streak.</li>
        <li><strong>Leaderboards</strong>: today, this week, this month and all time.</li>
        <li><strong>Top dezoom</strong>: most liked daily puzzles in the community.</li>
        <li><strong>Premium</strong>: ad-free experience and developer support.</li>
      </ul>
      <h2>7. Code of conduct</h2>
      <p>The following is prohibited, including but not limited to:</p>
      <ul>
        <li>Using bots, scripts or automated tools ;</li>
        <li>Exploiting bugs for unfair advantage ;</li>
        <li>Harassing other players ;</li>
        <li>Circumventing score or leaderboard systems ;</li>
        <li>Impersonating others or the publisher.</li>
      </ul>
      <h2>8. Premium subscription</h2>
      <p>
        Premium is a digital service tied to your account, non-transferable.
        It removes advertising on the site.
        Cancellation and refund terms: see <Link to="/cgv">Terms of sale</Link>.
      </p>
      <h2>9. Intellectual property</h2>
      <p>
        The game, puzzles, branding and content remain the property of PrograGames or its licensors
        (Pexels/Unsplash photographers — see <Link to="/credits">photo credits</Link>).
      </p>
      <h2>10. Personal data</h2>
      <p>
        Data processing is described in our{' '}
        <Link to="/confidentialite">privacy policy</Link>.
      </p>
      <h2>11. Liability</h2>
      <p>The game is provided &quot;as is&quot;. PrograGames does not guarantee uninterrupted operation.</p>
      <h2>12. Governing law</h2>
      <p>These Terms of Use are governed by <strong>French law</strong>.</p>
    </>
  );
}

function CGVBodyFr() {
  return (
    <>
      <h2>1. Vendeur</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        49000 Angers, France · SIREN 106 684 780 · RCS Angers<br />
        Contact :{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
      </p>
      <h2>2. Produits</h2>
      <p>Les achats portent sur du <strong>contenu numérique</strong> activable immédiatement :</p>
      <table className="legal-table">
        <thead>
          <tr><th>Produit</th><th>Description</th><th>Prix (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Dezoom Premium</td>
            <td>Expérience sans publicité, soutien au développement</td>
            <td>Selon l&apos;offre en vigueur (Stripe)</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Commande et paiement</h2>
      <p>Paiement via <strong>Stripe</strong> (carte bancaire). Compte connecté requis.</p>
      <h2>4. Livraison</h2>
      <p>Activation <strong>immédiate</strong> après confirmation du paiement.</p>
      <h2>5. Droit de rétractation</h2>
      <p>
        Délai de <strong>14 jours</strong>, sauf renonciation expresse pour contenu numérique délivré immédiatement
        (article L221-28).
      </p>
      <h2>6. Remboursements</h2>
      <ul>
        <li>Contenu non livré : remboursement ou activation.</li>
        <li>Contenu déjà activé : pas de remboursement sauf obligation légale.</li>
      </ul>
      <h2>7. Médiation</h2>
      <p>
        Médiateur <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>
      </p>
      <h2>8. Litiges</h2>
      <p>
        ODR :{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
      </p>
      <h2>9. CGU</h2>
      <p>L&apos;utilisation du jeu reste soumise aux <Link to="/cgu">CGU</Link>.</p>
    </>
  );
}

function CGVBodyEn() {
  return (
    <>
      <h2>1. Seller</h2>
      <p>
        <strong>Florian Augeard</strong> — PrograGames<br />
        49000 Angers, France · SIREN 106 684 780 · RCS Angers<br />
        Contact:{' '}
        <a href="https://progragames.com/contact.html" target="_blank" rel="noopener noreferrer">
          progragames.com/contact
        </a>
      </p>
      <h2>2. Products</h2>
      <p>Purchases cover <strong>digital content</strong> activated immediately on your account:</p>
      <table className="legal-table">
        <thead>
          <tr><th>Product</th><th>Description</th><th>Price (EUR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Dezoom Premium</td>
            <td>Ad-free experience, developer support</td>
            <td>As shown at checkout (Stripe)</td>
          </tr>
        </tbody>
      </table>
      <h2>3. Order and payment</h2>
      <p>Payment via <strong>Stripe</strong> (card). A logged-in account is required.</p>
      <h2>4. Delivery</h2>
      <p><strong>Immediate</strong> activation after payment confirmation.</p>
      <h2>5. Right of withdrawal</h2>
      <p>
        <strong>14-day</strong> withdrawal period, except where you expressly waive it for immediately delivered digital content
        (French Consumer Code L221-28).
      </p>
      <h2>6. Refunds</h2>
      <ul>
        <li>Undelivered content: refund or activation.</li>
        <li>Already activated content: no refund except where required by law.</li>
      </ul>
      <h2>7. Mediation</h2>
      <p>
        Mediator <strong>CM2C</strong> —{' '}
        <a href="https://www.cm2c.net/" target="_blank" rel="noopener noreferrer">www.cm2c.net</a>
      </p>
      <h2>8. Disputes</h2>
      <p>
        EU ODR platform:{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>
      </p>
      <h2>9. Terms of use</h2>
      <p>Use of the game remains subject to the <Link to="/cgu">Terms of use</Link>.</p>
    </>
  );
}

function MentionsBodyFr() {
  return (
    <>
      <h2>Éditeur du site et du jeu</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Entrepreneur individuel — <strong>PrograGames</strong><br />
        49000 Angers, France · SIREN 106 684 780
      </p>
      <h2>Directeur de la publication</h2>
      <p>Florian Augeard</p>
      <h2>Hébergement</h2>
      <p>
        <strong>Scaleway SAS</strong> — Paris, France —{' '}
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Propriété intellectuelle</h2>
      <p>
        Photos créditées sur <Link to="/credits">Crédits photos</Link> (Pexels, Unsplash).
      </p>
      <h2>Documents contractuels</h2>
      <ul>
        <li><Link to="/cgu">CGU</Link></li>
        <li><Link to="/cgv">CGV</Link></li>
        <li><Link to="/confidentialite">Politique de confidentialité</Link></li>
      </ul>
    </>
  );
}

function MentionsBodyEn() {
  return (
    <>
      <h2>Site and game publisher</h2>
      <p>
        <strong>Florian Augeard</strong><br />
        Sole proprietor — <strong>PrograGames</strong><br />
        49000 Angers, France · SIREN 106 684 780
      </p>
      <h2>Publication director</h2>
      <p>Florian Augeard</p>
      <h2>Hosting</h2>
      <p>
        <strong>Scaleway SAS</strong> — Paris, France —{' '}
        <a href="https://www.scaleway.com/" target="_blank" rel="noopener noreferrer">www.scaleway.com</a>
      </p>
      <h2>Intellectual property</h2>
      <p>
        Photos credited on <Link to="/credits">Photo credits</Link> (Pexels, Unsplash).
      </p>
      <h2>Legal documents</h2>
      <ul>
        <li><Link to="/cgu">Terms of use</Link></li>
        <li><Link to="/cgv">Terms of sale</Link></li>
        <li><Link to="/confidentialite">Privacy policy</Link></li>
      </ul>
    </>
  );
}

function PrivacyBodyFr() {
  return (
    <>
      <h2>1. Responsable du traitement</h2>
      <p>Florian Augeard — PrograGames, 49000 Angers, France.</p>
      <h2>2. Données collectées</h2>
      <ul>
        <li><strong>Compte</strong> : email, pseudo, avatar ;</li>
        <li><strong>Gameplay</strong> : scores, série ;</li>
        <li><strong>Achats</strong> : identifiants Stripe (pas de numéro de carte) ;</li>
        <li><strong>Technique</strong> : IP, logs serveur.</li>
      </ul>
      <h2>3. Finalités</h2>
      <p>Exécution du contrat, sécurité, obligations légales.</p>
      <h2>4. Sous-traitants</h2>
      <ul>
        <li><strong>Stripe</strong> (paiements), <strong>Scaleway</strong> (hébergement), <strong>Mailjet</strong> (emails),</li>
        <li><strong>Google Ireland Limited</strong> (Google AdSense — publicité et mesure d&apos;audience).</li>
      </ul>
      <h2>5. Durées</h2>
      <p>Compte actif ; achats : 10 ans (comptabilité) ; logs : quelques mois.</p>
      <h2>6. Cookies et traceurs</h2>
      <p>
        Pour les visiteurs situés dans l&apos;EEE, au Royaume-Uni ou en Suisse, un message de consentement
        certifié Google (CMP) te permet d&apos;accepter, de refuser ou de gérer les cookies publicitaires.
        Ton choix est enregistré par cette plateforme conformément au framework IAB TCF.
      </p>
      <ul>
        <li>
          <strong>Essentiels</strong> : session de jeu, préférences de langue, authentification
          (localStorage). Base légale : intérêt légitime / exécution du contrat.
        </li>
        <li>
          <strong>Publicitaires (Google AdSense)</strong> : cookies de personnalisation et de mesure
          déposés par Google lorsque tu acceptes le bandeau. Base légale : consentement.
          Durée : jusqu&apos;à 13 mois selon les cookies Google.
        </li>
      </ul>
      <p>
        Tu peux modifier ton choix via le message de consentement Google affiché sur le site.
        Pour les publicités Google :{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          paramètres Google Ads
        </a>
        {' '}·{' '}
        <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
          technologies publicitaires Google
        </a>
        .
      </p>
      <h2>7. Tes droits (RGPD)</h2>
      <p>
        Accès, rectification, effacement — contact PrograGames. CNIL :{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Mineurs</h2>
      <p>16 ans et plus (voir <Link to="/cgu">CGU</Link>).</p>
    </>
  );
}

function PrivacyBodyEn() {
  return (
    <>
      <h2>1. Data controller</h2>
      <p>Florian Augeard — PrograGames, 49000 Angers, France.</p>
      <h2>2. Data collected</h2>
      <ul>
        <li><strong>Account</strong>: email, display name, avatar ;</li>
        <li><strong>Gameplay</strong>: scores, streak ;</li>
        <li><strong>Purchases</strong>: Stripe identifiers (no card numbers stored) ;</li>
        <li><strong>Technical</strong>: IP, server logs.</li>
      </ul>
      <h2>3. Purposes</h2>
      <p>Contract performance, security, legal obligations.</p>
      <h2>4. Processors</h2>
      <ul>
        <li><strong>Stripe</strong> (payments), <strong>Scaleway</strong> (hosting), <strong>Mailjet</strong> (email),</li>
        <li><strong>Google Ireland Limited</strong> (Google AdSense — advertising and audience measurement).</li>
      </ul>
      <h2>5. Retention</h2>
      <p>Active account lifetime ; purchases: 10 years ; logs: a few months.</p>
      <h2>6. Cookies and trackers</h2>
      <p>
        For visitors in the EEA, UK or Switzerland, a Google-certified consent message (CMP) lets you
        accept, reject or manage advertising cookies. Your choice is stored by this platform under the IAB TCF framework.
      </p>
      <ul>
        <li>
          <strong>Essential</strong>: game session, language preferences, authentication
          (localStorage). Legal basis: legitimate interest / contract performance.
        </li>
        <li>
          <strong>Advertising (Google AdSense)</strong>: personalization and measurement cookies
          set by Google when you accept the banner. Legal basis: consent.
          Duration: up to 13 months depending on Google cookies.
        </li>
      </ul>
      <p>
        You can change your choice via the Google consent message shown on the site.
        For Google ads:{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          Google Ads settings
        </a>
        {' '}·{' '}
        <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
          Google advertising technologies
        </a>
        .
      </p>
      <h2>7. Your rights (GDPR)</h2>
      <p>
        Access, rectification, erasure — contact PrograGames. CNIL:{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">cnil.fr</a>.
      </p>
      <h2>8. Minors</h2>
      <p>Aged 16 and over (see <Link to="/cgu">Terms of use</Link>).</p>
    </>
  );
}

const BODIES: Record<string, Record<Locale, () => ReactElement>> = {
  cgu: { fr: CGUBodyFr, en: CGUBodyEn },
  cgv: { fr: CGVBodyFr, en: CGVBodyEn },
  mentions: { fr: MentionsBodyFr, en: MentionsBodyEn },
  privacy: { fr: PrivacyBodyFr, en: PrivacyBodyEn },
};

export function LegalBody({ page }: { page: keyof typeof BODIES }) {
  const { locale } = useLocale();
  const Body = BODIES[page][locale];
  return <Body />;
}
